// externals
import React, { memo, useCallback, useMemo } from 'react';

// libraries
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// types
import type { FeeCategoryItemsProps } from './FeeCategoryItems';

// components
import { FeesFormItems } from './FeesFormItems';
import {
    CategoryHeader,
    CategoryHeaderActions,
    CategoryTitle,
    FeesHeaderContainer,
    FieldHeader
} from './FeesFormCategoryItems.style';
import { Box } from '@interstate/components/Box';

//utils
import { useBaseDealBreakpoint } from '../../utils/useBaseDealBreakpoint';
import { useFeesFormContext } from './useFeesFormContext';
import { getFeeDetailsRows } from './utils';
import { DELETED_BY_ACTION_FEE_AMOUNT } from './constants';
import RegistrationField from './RegistrationType';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { ActionStyle } from './FeesForm.style';
import { Action } from '@interstate/components/Action/Action/Action';
import { paymentActionCreators } from '@makemydeal/dr-offer-redux';

export const FeesFormCategoryItems: React.FC<Omit<FeeCategoryItemsProps, 'offerType'>> = ({
    categoryTitle,
    feeItems,
    dataTestId,
    categoryType
}) => {
    const dispatch = useDispatch();
    const baseBreakPoint = useBaseDealBreakpoint();
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isInlineTaxedIndicatorEnabled = useSelector(featureToggleSelectors.isInlineTaxedIndicatorEnabled);
    const isGovernmentFeesCategory = categoryType === 'Government';

    const { isLease } = useFeesFormContext();
    const availableRows = getFeeDetailsRows({ feeItems, isLease: !!isLease }).filter(
        (detail) => detail.amount !== DELETED_BY_ACTION_FEE_AMOUNT || detail.isEdited !== false
    );

    const clearGovFees = useCallback(() => {
        dispatch(paymentActionCreators.updateUseCalculatedGovFees({ useCalculatedGovFees: false }));
    }, [dispatch]);

    const renderFeesFormHeader = () => {
        return (
            availableRows.length > 0 && (
                <FeesHeaderContainer
                    data-testid={'edit-fees-fields-header'}
                    baseBreakPoint={baseBreakPoint}
                    isDealCentralMultiScenario={isDealCentralMultiScenario}
                >
                    <FieldHeader>Code *</FieldHeader>
                    <FieldHeader isName>Name *</FieldHeader>
                    <FieldHeader isAmount>Amount *</FieldHeader>
                    <FieldHeader isLease={isLease} isEmpty />
                    {isInlineTaxedIndicatorEnabled && <FieldHeader isEmpty isTaxedColumn />}
                </FeesHeaderContainer>
            )
        );
    };

    return useMemo(
        () => (
            <Box data-testid={dataTestId} marginTop={3} sx={{ overflowX: 'auto' }} className="deal-fees-group">
                <CategoryHeader>
                    <CategoryTitle variant="h5">
                        {categoryTitle}
                        {isGovernmentFeesCategory && <RegistrationField />}
                    </CategoryTitle>
                    {isGovernmentFeesCategory && (
                        <CategoryHeaderActions>
                            <ActionStyle>
                                <Action size="sm" onClick={clearGovFees} data-testid={`clear-gov-fees-action-${categoryType}`}>
                                    {`Clear Government Fees`}
                                </Action>
                            </ActionStyle>
                        </CategoryHeaderActions>
                    )}
                </CategoryHeader>
                {renderFeesFormHeader()}
                <FeesFormItems feeItems={feeItems} categoryType={categoryType} />
            </Box>
        ),
        [
            baseBreakPoint,
            categoryTitle,
            categoryType,
            clearGovFees,
            dataTestId,
            feeItems,
            isDealCentralMultiScenario,
            isGovernmentFeesCategory,
            isInlineTaxedIndicatorEnabled,
            isLease
        ]
    );
};

export default memo(FeesFormCategoryItems);
